import { Card, Checkbox, FormControlLabel, IconButton, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';
import InfoIcon from '@mui/icons-material/Info';
import styles from "./styles.module.css"
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { IOStatusBadge } from '../../../components/IOStatusBadge/IOStatusBadge';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { DeviceData } from '../../../types/device';

interface Props {
    closeDrawer: (state: boolean) => void;
    systemDetail: DeviceData;
}

const DryContactIOSetting: React.FC<Props> = ({ closeDrawer, systemDetail }) => {

    const { t } = useLanguageLocale();
    const handleCloseClick = () => {
        closeDrawer(false);
    };
    const inputConfigDetailLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.REST_ORDER',
        '4': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE1',
        '5': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE2',
        '6': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE3',
        '7': 'DRY_CONTACT.IOSTATUS.MAINTAINED_MODE_OFFLINE4',
        '8': 'DRY_CONTACT.IOSTATUS.START_FUNCTIONAL_TEST',
        '9': 'DRY_CONTACT.IOSTATUS.STOP_FT_DT',
        '10': 'DRY_CONTACT.IOSTATUS.EXTERNAL_FAILURE',
        '11': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE1',
        '12': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE2',
        '13': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE3',
        '14': 'DRY_CONTACT.IOSTATUS.ACTIVATE_IA_MODELINE4',
        '15': 'DRY_CONTACT.NORMALLY_CLOSED',
    };
    const outputConfigDetailLabels: { [key: string]: string } = {
        '1': 'DRY_CONTACT.IOSTATUS.NOT_CONFIGURED',
        '2': 'DRY_CONTACT.NORMALLY_OPEN',
        '3': 'DRY_CONTACT.IOSTATUS.ONE_LUMINAIRE_FAILURE',
        '4': 'DRY_CONTACT.IOSTATUS.THREE_CONSECUTIVE_LUMINAIRE_FAILURE',
        '5': 'DUALSMART.DS_COMMUNICATION_FAILURE',
        '6': 'DUALSMART.DS_BATTERY_OPERATION',
        '7': 'DRY_CONTACT.IOSTATUS.FUNCTIONAL_TEST',
        '8': 'DEVICE_DETAILS.DURATION_TEST',
        '9': 'DRY_CONTACT.IOSTATUS.CHARGING_FAILURE',
        '10': 'DRY_CONTACT.IOSTATUS.FAILURE_FT_DT',
        '11': 'DRY_CONTACT.NORMALLY_CLOSED',
    };

    const renderConfigDetailsOutput = (details?: number[]) => {
        if (!details || details.length === 0) return t('MANAGE_USERS.NOT_APPLICABLE_SLASH');
        const firstLine = details
            .filter(val => val === 2 || val === 11)
            .map(val => t(outputConfigDetailLabels[val.toString()] || val.toString()))
            .join(', ');
        const secondLine = details
            .filter(val => val !== 2 && val !== 11)
            .map(val => t(outputConfigDetailLabels[val.toString()] || val.toString()))
            .join(', ');

        return (
            <>
                <span>{firstLine || t('MANAGE_USERS.NOT_APPLICABLE_SLASH')}</span>
                {secondLine && (
                    <Typography variant="body2" component="div" style={{ display: 'block' }}>
                        {secondLine}
                    </Typography>
                )}
            </>
        );
    };

    const renderConfigDetailsInput = (details?: number[]) => {
        if (!details || details.length === 0) return t('MANAGE_USERS.NOT_APPLICABLE_SLASH');
        const firstLine = details
            .filter(val => val === 2 || val === 15)
            .map(val => t(inputConfigDetailLabels[val.toString()] || val.toString()))
            .join(', ');
        const secondLine = details
            .filter(val => val !== 2 && val !== 15)
            .map(val => t(inputConfigDetailLabels[val.toString()] || val.toString()))
            .join(', ');
        return (
            <>
                <span>{firstLine || t('MANAGE_USERS.NOT_APPLICABLE_SLASH')}</span>
                {secondLine && (
                    <Typography variant="body2" component="div" style={{ display: 'block' }}>
                        {secondLine}
                    </Typography>
                )}
            </>
        );
    };
    const mappedRecords = [
        {
            contentItem: [
                {
                    type: "input",
                    label: t('DRY_CONTACT.INPUT_1'),
                    description: renderConfigDetailsInput(systemDetail.dryContactInput?.[0]?.configDetails),
                    status: systemDetail.dryContactInput?.[0]?.status !== undefined ? systemDetail.dryContactInput[0].status.toString() : ''
                },
                {
                    type: "output",
                    label: t('DRY_CONTACT.OUTPUT_1'),
                    description: renderConfigDetailsOutput(systemDetail.dryContactOutput?.[0]?.configDetails),
                    status: systemDetail.dryContactOutput?.[0]?.status !== undefined ? systemDetail.dryContactOutput[0].status.toString() : ''
                }
            ]
        },
        {
            contentItem: [
                {
                    type: "input",
                    label: t('DRY_CONTACT.INPUT_2'),
                    description: renderConfigDetailsInput(systemDetail.dryContactInput?.[1]?.configDetails),
                    status: systemDetail.dryContactInput?.[1]?.status !== undefined ? systemDetail.dryContactInput[1].status.toString() : ''
                },
                {
                    type: "output",
                    label: t('DRY_CONTACT.OUTPUT_2'),
                    description: renderConfigDetailsOutput(systemDetail.dryContactOutput?.[1]?.configDetails),
                    status: systemDetail.dryContactOutput?.[1]?.status !== undefined ? systemDetail.dryContactOutput[1].status.toString() : ''
                }
            ]
        },
        {
            contentItem: [
                {
                    type: t('DRY_CONTACT.S1_S2'),
                    label: t('DRY_CONTACT.S1_S2'),
                    description: t('MANAGE_USERS.NOT_APPLICABLE_SLASH'),
                    status: systemDetail.dryContactS1S2 ? "1" : "0"
                },
            ]
        }
    ];


    return (
        <>
            <Card className={styles.card} data-testid="dry-contact-io-settings">
                <div className={styles.block} >
                    <div className={styles.title}>
                        <h2 className={styles.title}>{systemDetail.systemName}</h2>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseClick}
                            sx={{
                                color: (theme) => theme.palette.grey[500],
                            }}>
                            <CloseOutlinedIcon />
                        </IconButton>
                    </div>
                    <p style={{ padding: 0, margin: 0, color: '#424E54' }}>{t('DRY_CONTACT.DRY_CONTACT_SETTINGS_READ_ONLY')}</p>
                    <p style={{
                        fontSize: '12px',
                        display: 'flex',
                        columnGap: '5px',
                        alignItems: 'flex-start'
                    }}>
                        <InfoIcon style={{ color: "#0088F2", fontSize: "14px" }} />
                        <span style={{ lineHeight: '1.3' }}>{t('DRY_CONTACT.DRY_CONTACT_SETTINGS_DESCRIPTION')}</span>
                    </p>
                    <div className={styles.detailBlock}>
                        <h3 style={{ fontSize: '16px', margin: 0, padding: 0, color: '#424E54', fontWeight: 600 }}>{t('DRY_CONTACT.DRY_CONTACT_DETAILS')}</h3>
                        {
                            mappedRecords.map((record, index) => (
                                <div key={index} className={styles.row}>
                                    {record.contentItem.map((item, i) => (
                                        <div key={i} className={styles.item}>
                                            <h3
                                                style={{
                                                    fontSize: '16px',
                                                    margin: 0,
                                                    padding: 0,
                                                    color: '#424E54',
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between', // <-- pushes icon to the end
                                                    width: '100%',
                                                }}
                                            >
                                                <span>{item.label}</span>
                                                <IOStatusBadge status={item.status !== undefined && item.status !== null ? item.status.toString() : ''} />
                                            </h3>
                                            <span style={{ fontSize: '14px', color: '#424E54', fontWeight: 400 }}>{item.description}</span>
                                        </div>
                                    ))}
                                </div>
                            ))
                        }
                    </div>
                    {/* Commented code not required for default settings till edit functionality is implemented */}
                    {/* <div className={styles.settingBlock} style={{
                        marginTop: '15px',
                        borderBottom: '1px solid #8080805e',
                        marginLeft: '-10px',
                        marginRight: '-10px',
                        padding: '0px 10px'
                    }}>
                        <h3 style={{ fontSize: '16px', margin: 0, padding: 0, color: '#424E54', fontWeight: 600 }}>{t('DRY_CONTACT.DEFAULT_SETTINGS')}</h3>
                        <h5 style={{ fontSize: '14px', margin: 0, padding: 0, marginTop: '15px', color: '#424E54', fontWeight: 600 }}>{t('DEVICE_DETAILS.ACTIVE')}</h5>
                        <RadioGroup name="controlled-radio-buttons-group" style={{ marginTop: '15px' }}>
                            <FormControlLabel style={{ fontSize: '14px', margin: 0, padding: 0, color: '#424E54', fontWeight: 400 }} value="open" control={<Radio />} label={t('DRY_CONTACT.NORMALLY_OPEN')} />
                            <FormControlLabel style={{ fontSize: '14px', margin: 0, padding: 0, color: '#424E54', fontWeight: 400 }} value="closed" control={<Radio />} label={t('DRY_CONTACT.NORMALLY_CLOSED')} />
                        </RadioGroup>
                    </div>
                    <div className={styles.contactOutputBlock} style={{
                        marginTop: '15px',
                        borderBottom: '1px solid #8080805e',
                        marginLeft: '-10px',
                        marginRight: '-10px',
                        padding: '0px 10px'
                    }}>
                        <h3 style={{ fontSize: '16px', margin: 0, padding: 0, color: '#424E54', fontWeight: 600 }}>{t('DRY_CONTACT.DRY_CONTACT_OUTPUT')}</h3>
                        <h5 style={{ fontSize: '14px', margin: 0, padding: 0, marginTop: '15px', color: '#424E54', fontWeight: 600 }}>{t('DEVICE_DETAILS.ACTIVE')}</h5>
                        <div style={{ marginTop: '15px' }}>
                            <Checkbox color="default" /> {t('DRY_CONTACT.NORMALLY_OPEN')}
                        </div>
                    </div>
                    <div className={styles.settingBlock} style={{ marginTop: '15px' }}>
                        <h3 style={{ fontSize: '16px', margin: 0, padding: 0, color: '#424E54', fontWeight: 600 }}>{t('DRY_CONTACT.DEFAULT_SETTINGS')}</h3>
                        <h5 style={{ fontSize: '14px', margin: 0, padding: 0, marginTop: '15px', color: '#424E54', fontWeight: 600 }}>{t('DRY_CONTACT.DRY_CONTACT_INPUT')}</h5>
                        <div style={{ marginTop: '15px' }}>
                            <Checkbox color="default" /> {t('DRY_CONTACT.NORMALLY_OPEN')}
                        </div>
                        <div>
                            <Checkbox color="default" /> {t('DRY_CONTACT.NORMALLY_CLOSED')}
                        </div>
                    </div> */}
                </div>
            </Card>
        </>
    );
};

export default DryContactIOSetting;
